// src/components/vault/documents/id-validation/IDValidationLoader.tsx

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { CheckCircle2, User, FileText, Check } from "lucide-react";
import { cn } from "../../../../lib/utils";
import {
  ID_VALIDATION_STEPS,
  DOCUMENT_TYPE_LABELS,
  getActiveStepIndex,
  SUB_MESSAGE_ROTATION_MS,
  LONG_WAIT_THRESHOLD_MS,
  LONG_WAIT_MESSAGE,
} from "./constants";
import type { ValidationStepDefinition } from "./constants";
import type { IDValidationLoaderProps } from "./types";

// ============================================================================
// SHARED: ValidationStepsList (used by both ID and ProofOfAddress loaders)
// ============================================================================

interface ValidationStepsListProps {
  steps: ValidationStepDefinition[];
  progress: number;
  isComplete: boolean;
}

export function ValidationStepsList({
  steps,
  progress,
  isComplete,
}: ValidationStepsListProps) {
  const activeStepIndex = getActiveStepIndex(progress, steps);
  const activeStep = steps[activeStepIndex];

  // Sub-message rotation
  const prevStepRef = useRef(activeStepIndex);
  const [subMessageIndex, setSubMessageIndex] = useState(0);

  useEffect(() => {
    if (activeStepIndex !== prevStepRef.current) {
      setSubMessageIndex(0);
      prevStepRef.current = activeStepIndex;
    }
  }, [activeStepIndex]);

  useEffect(() => {
    if (isComplete) return;
    const interval = setInterval(() => {
      setSubMessageIndex((prev) => {
        const maxIndex = activeStep.subMessages.length - 1;
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, SUB_MESSAGE_ROTATION_MS);
    return () => clearInterval(interval);
  }, [activeStepIndex, isComplete, activeStep.subMessages.length]);

  // Long wait fallback
  const startTimeRef = useRef(Date.now());
  const [isLongWait, setIsLongWait] = useState(false);

  useEffect(() => {
    if (isComplete) return;
    const interval = setInterval(() => {
      if (Date.now() - startTimeRef.current > LONG_WAIT_THRESHOLD_MS) {
        setIsLongWait(true);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [isComplete]);

  return (
    <div className="w-full max-w-xs mx-auto mt-6 space-y-0">
      {steps.map((step, index) => {
        const isStepComplete = isComplete || progress >= step.endProgress;
        const isStepActive = !isComplete && activeStepIndex === index;
        const isStepPending = !isStepComplete && !isStepActive;
        const StepIcon = step.icon;

        return (
          <div key={step.key} className="flex items-start gap-3 py-2 relative">
            {/* Connector line */}
            {index < steps.length - 1 && (
              <div
                className={cn(
                  "absolute left-[14px] top-[34px] w-0.5 h-[calc(100%-18px)] transition-colors duration-300",
                  isStepComplete ? "bg-emerald-200" : "bg-slate-100"
                )}
              />
            )}

            {/* Icon circle */}
            <motion.div
              className={cn(
                "relative z-10 flex items-center justify-center w-[30px] h-[30px] rounded-full flex-shrink-0 transition-colors duration-300",
                isStepComplete && "bg-emerald-500",
                isStepActive && "bg-[#28397a]",
                isStepPending && "bg-slate-100"
              )}
              animate={
                isStepActive
                  ? {
                      boxShadow: [
                        "0 0 0 0px rgba(40,57,122,0.3)",
                        "0 0 0 8px rgba(40,57,122,0)",
                        "0 0 0 0px rgba(40,57,122,0.3)",
                      ],
                    }
                  : {}
              }
              transition={
                isStepActive
                  ? { duration: 2, repeat: Infinity, ease: "easeInOut" }
                  : {}
              }
            >
              {isStepComplete ? (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 15,
                    delay: isComplete ? index * 0.1 : 0,
                  }}
                >
                  <Check className="w-4 h-4 text-white" strokeWidth={2.5} />
                </motion.div>
              ) : (
                <StepIcon
                  className={cn(
                    "w-4 h-4",
                    isStepActive ? "text-white" : "text-slate-300"
                  )}
                  strokeWidth={1.5}
                />
              )}
            </motion.div>

            {/* Label + sub-message */}
            <div className="flex-1 min-w-0 pt-[3px]">
              <span
                className={cn(
                  "text-sm leading-tight block transition-colors duration-300",
                  isStepComplete && "text-slate-600",
                  isStepActive && "text-slate-900 font-semibold",
                  isStepPending && "text-slate-300"
                )}
              >
                {step.label}
              </span>

              {isStepActive && (
                <div className="h-5 overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={`${step.key}-${isLongWait ? "long" : subMessageIndex}`}
                      className="text-xs text-slate-400 mt-0.5 block truncate"
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.2 }}
                    >
                      {isLongWait
                        ? LONG_WAIT_MESSAGE
                        : step.subMessages[
                            subMessageIndex % step.subMessages.length
                          ]}
                    </motion.span>
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// ID VALIDATION LOADER
// ============================================================================

export const IDValidationLoader: React.FC<IDValidationLoaderProps> = ({
  progress,
  documentType,
}) => {
  const isComplete = progress >= 100;
  const documentLabel = DOCUMENT_TYPE_LABELS[documentType] || documentType;

  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 w-full max-w-sm mx-auto">
      {/* Document card with scanning effect */}
      <div className="relative mb-8">
        {!isComplete && (
          <div className="absolute -inset-3 rounded-3xl bg-[#28397a]/5 blur-xl animate-pulse" />
        )}

        <motion.div
          className={cn(
            "relative w-44 h-28 rounded-2xl border-2 overflow-hidden flex items-center justify-center",
            isComplete
              ? "border-emerald-300 bg-emerald-50 shadow-lg shadow-emerald-100"
              : "border-[#28397a]/20 bg-white shadow-xl shadow-[#28397a]/10"
          )}
          animate={
            isComplete
              ? { borderColor: "rgb(110 231 183)", backgroundColor: "rgb(236 253 245)" }
              : {}
          }
          transition={{ duration: 0.7 }}
        >
          {!isComplete && (
            <div
              className="absolute inset-x-0 h-8 bg-gradient-to-b from-[#28397a]/10 via-[#28397a]/5 to-transparent pointer-events-none"
              style={{ animation: "scanLine 2s ease-in-out infinite" }}
            />
          )}

          <AnimatePresence mode="wait">
            {!isComplete ? (
              <motion.div
                key="scanning"
                className="flex items-center gap-3 px-4"
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                <div className="w-10 h-12 rounded-lg bg-[#28397a]/8 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-[#28397a]/40" strokeWidth={1.5} />
                </div>
                <div className="flex flex-col gap-1.5 flex-1">
                  <div className="h-2 w-16 rounded-full bg-[#28397a]/10" />
                  <div className="h-1.5 w-12 rounded-full bg-[#28397a]/6" />
                  <div className="h-1.5 w-20 rounded-full bg-[#28397a]/6" />
                  <div className="h-1.5 w-8 rounded-full bg-[#28397a]/6" />
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="complete"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.3 }}
              >
                <CheckCircle2
                  className="w-12 h-12 text-emerald-500"
                  strokeWidth={1.5}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Title and badge */}
      <div className="flex flex-col items-center mb-2">
        <AnimatePresence mode="wait">
          <motion.h3
            key={isComplete ? "done" : "loading"}
            className="text-xl font-semibold text-slate-900 mb-1.5 text-center tracking-tight"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
          >
            {isComplete ? "Validacion completada" : "Validando documento"}
          </motion.h3>
        </AnimatePresence>
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-xs font-medium text-slate-500">
          <FileText className="w-3 h-3" />
          {documentLabel}
        </span>
      </div>

      {/* Stepped progress */}
      <ValidationStepsList
        steps={ID_VALIDATION_STEPS}
        progress={progress}
        isComplete={isComplete}
      />

      {/* Overall progress bar */}
      <div className="w-full max-w-xs mx-auto mt-5">
        <div className="w-full h-1 rounded-full bg-slate-100 overflow-hidden">
          <motion.div
            className={cn(
              "h-full rounded-full",
              isComplete ? "bg-emerald-500" : "bg-[#28397a]"
            )}
            animate={{ width: `${isComplete ? 100 : progress}%` }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
        </div>
      </div>

      <style>{`
        @keyframes scanLine {
          0%, 100% { top: -2rem; }
          50% { top: calc(100% - 2rem); }
        }
      `}</style>
    </div>
  );
};
